import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { accountsAPI } from '../lib/api';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import {
  Shield, Loader2, ChevronRight, ArrowDownRight, ArrowUpRight,
  Lock, AlertTriangle, ExternalLink, CheckCircle2,
} from 'lucide-react';

function fmtCurrency(v) {
  if (!v && v !== 0) return '—';
  const sign = v < 0 ? '-' : v > 0 ? '+' : '';
  return `${sign}\u20B9${Math.abs(v).toLocaleString('en-IN')}`;
}

const ENTITY_LABELS = {
  SALES_UPLOAD: 'Sales Upload',
  EXPENSE_EXCLUSION: 'Expense',
};

const ACTION_LABELS = {
  SOFT_DELETE: 'Soft Delete',
  EXCLUDE_IN_ACCOUNTS: 'Exclude in Accounts',
};

/**
 * Shared Deletion Request Initiation Modal.
 *
 * Props:
 *   open         - boolean
 *   onClose      - () => void
 *   entityType   - 'SALES_UPLOAD' | 'EXPENSE_EXCLUSION'
 *   entityId     - string (upload_id or bill_id)
 *   actionType   - 'SOFT_DELETE' | 'EXCLUDE_IN_ACCOUNTS'
 *   entityLabel  - string for display (e.g. "Sales Upload: report.csv")
 *   onCreated    - () => void (callback after successful creation)
 */
export function DeletionRequestModal({ open, onClose, entityType, entityId, actionType, entityLabel, onCreated }) {
  const navigate = useNavigate();
  const [step, setStep] = useState('reason'); // 'reason' | 'preview' | 'submitted'
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [createdRequest, setCreatedRequest] = useState(null);
  const [error, setError] = useState(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setStep('reason');
      setReason('');
      setPreview(null);
      setError(null);
      setCreatedRequest(null);
    }
  }, [open]);

  const loadPreview = useCallback(async () => {
    setLoadingPreview(true);
    setError(null);
    try {
      const { data } = await accountsAPI.previewDeletion({
        entity_type: entityType,
        entity_id: entityId,
        action_type: actionType,
      });
      setPreview(data);
      setStep('preview');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to compute impact preview');
    } finally {
      setLoadingPreview(false);
    }
  }, [entityType, entityId, actionType]);

  const handleSubmit = async () => {
    if (!reason.trim()) { toast.error('Reason is required'); return; }
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await accountsAPI.createDeleteRequest({
        entity_type: entityType,
        entity_id: entityId,
        action_type: actionType,
        reason: reason.trim(),
      });
      setCreatedRequest(data);
      setStep('submitted');
      toast.success('Deletion request submitted for approval');
      onCreated?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create request');
    } finally {
      setSubmitting(false);
    }
  };

  const impactMetrics = useMemo(() => {
    const impactSnapshot = preview?.impact_snapshot;
    if (!impactSnapshot?.summary) return [];
    const { summary } = impactSnapshot;
    return [
      { label: 'Revenue', value: summary.revenue_change, warn: summary.revenue_change < 0 },
      { label: 'Expense', value: summary.expense_change, warn: false },
      { label: 'Gross Profit', value: summary.gross_profit_change, warn: summary.gross_profit_change < 0 },
      { label: 'Margin (bps)', value: summary.gross_margin_change_bps, isBps: true, warn: summary.gross_margin_change_bps < 0 },
      { label: 'Cash Impact', value: summary.cash_in_hand_change, warn: summary.cash_in_hand_change < 0 },
      { label: 'Transactions', value: summary.transaction_count, isCount: true },
      { label: 'Stl Matches', value: summary.settlement_matches_affected, isCount: true, warn: summary.settlement_matches_affected > 0 },
    ].filter(m => m.value !== undefined && m.value !== null && m.value !== 0);
  }, [preview?.impact_snapshot]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="deletion-request-modal">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-400" />
            <DialogTitle className="font-manrope tracking-tight">
              {entityType === 'EXPENSE_EXCLUSION' ? 'Request Exclusion' : 'Request Deletion'}
            </DialogTitle>
          </div>
          <DialogDescription>
            This creates a governed financial correction request requiring approval before execution.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Entity info */}
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="text-xs">{ENTITY_LABELS[entityType] || entityType}</Badge>
            <Badge variant="outline" className="text-xs">{ACTION_LABELS[actionType] || actionType}</Badge>
          </div>
          <p className="text-sm font-medium" data-testid="entity-label">{entityLabel}</p>

          {error && (
            <div className="flex items-center gap-2 bg-rose-400/10 border border-rose-400/20 rounded-lg p-3">
              <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0" />
              <p className="text-sm text-rose-400">{error}</p>
            </div>
          )}

          {/* Step 1: Reason */}
          {step === 'reason' && (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Reason for {entityType === 'EXPENSE_EXCLUSION' ? 'exclusion' : 'deletion'} *</label>
                <Textarea
                  placeholder="E.g., Duplicate upload, incorrect vendor billing, test data..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="min-h-[80px]"
                  data-testid="deletion-reason-input"
                />
              </div>
              <Button
                className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                disabled={!reason.trim() || loadingPreview}
                onClick={loadPreview}
                data-testid="preview-impact-btn"
              >
                {loadingPreview ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
                Preview Financial Impact
              </Button>
            </div>
          )}

          {/* Step 2: Preview */}
          {step === 'preview' && preview && (
            <div className="space-y-4">
              {/* Impact summary */}
              <div className="bg-card/50 border border-border/50 rounded-xl p-4 space-y-3" data-testid="impact-preview-section">
                <h4 className="font-manrope font-semibold text-sm tracking-tight">Financial Impact</h4>
                <div className="grid grid-cols-2 gap-3">
                  {impactMetrics.map((m) => (
                    <div key={m.label} className="bg-background/50 rounded-lg border border-border/50 p-2.5">
                      <p className="text-xs text-muted-foreground mb-0.5">{m.label}</p>
                      <p className={cn('font-mono text-sm tabular-nums', m.warn ? 'text-rose-400' : 'text-foreground')}>
                        {m.isCount ? m.value : m.isBps ? `${m.value > 0 ? '+' : ''}${m.value || 0} bps` : fmtCurrency(m.value)}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Forecast impact */}
                {preview.impact_snapshot?.forecast_impact?.target_gap_change !== 0 && (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-muted-foreground">Forecast:</span>
                    {preview.impact_snapshot.forecast_impact.landing_status_before && (
                      <span className="flex items-center gap-1">
                        {preview.impact_snapshot.forecast_impact.landing_status_before}
                        <ChevronRight className="w-3 h-3" />
                        <span className="text-amber-400">{preview.impact_snapshot.forecast_impact.landing_status_after}</span>
                      </span>
                    )}
                  </div>
                )}

                {/* Alerts */}
                {preview.impact_snapshot?.alerts_expected_to_change?.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-muted-foreground">Alerts affected:</span>
                    {preview.impact_snapshot.alerts_expected_to_change.map((a) => (
                      <Badge key={a} variant="outline" className="text-xs bg-rose-400/10 text-rose-400 border-rose-400/20">{a}</Badge>
                    ))}
                  </div>
                )}

                {/* Locked period */}
                {preview.impact_snapshot?.affects_locked_period && (
                  <div className="flex items-center gap-2 bg-rose-400/10 border border-rose-400/20 rounded-lg p-2.5" data-testid="locked-period-warning">
                    <Lock className="w-4 h-4 text-rose-400 shrink-0" />
                    <p className="text-xs text-rose-400">
                      {preview.impact_snapshot.locked_period_behavior === 'BLOCKED'
                        ? 'Blocked: Affects a locked tax period.'
                        : 'Warning: Affects a locked tax period.'}
                    </p>
                  </div>
                )}
              </div>

              {/* Reason display */}
              <div className="bg-background/50 rounded-lg border border-border/50 p-2.5">
                <p className="text-xs text-muted-foreground mb-0.5">Reason</p>
                <p className="text-sm">{reason}</p>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setStep('reason')} className="flex-1" data-testid="back-to-reason-btn">
                  Edit Reason
                </Button>
                <Button
                  size="sm"
                  className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                  disabled={submitting || (preview.impact_snapshot?.affects_locked_period && preview.impact_snapshot?.locked_period_behavior === 'BLOCKED')}
                  onClick={handleSubmit}
                  data-testid="submit-request-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Shield className="w-4 h-4 mr-1" />}
                  Send for Approval
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Submitted */}
          {step === 'submitted' && createdRequest && (
            <div className="space-y-4 text-center py-4">
              <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto" />
              <div>
                <p className="text-sm font-medium text-emerald-400">Request Submitted</p>
                <p className="text-xs text-muted-foreground mt-1">An approver must review and approve this request before execution.</p>
              </div>
              <div className="bg-background/50 rounded-lg border border-border/50 p-3 text-left">
                <p className="text-xs text-muted-foreground">Request ID</p>
                <p className="font-mono text-xs truncate">{createdRequest.request_id}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => { onClose(); navigate('/deletion-requests'); }}
                data-testid="view-request-btn"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                View in Approval Queue
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Badge shown on entity pages when an active deletion request exists.
 *
 * Props:
 *   entityType - 'SALES_UPLOAD' | 'EXPENSE_EXCLUSION'
 *   entityId   - string
 */
export function ActiveDeleteRequestBadge({ entityType, entityId }) {
  const navigate = useNavigate();
  const [activeReq, setActiveReq] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!entityType || !entityId) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await accountsAPI.checkEntityDeleteRequest({ entity_type: entityType, entity_id: entityId });
        if (!cancelled) setActiveReq(data.has_active_request ? data.active_request : null);
      } catch {
        // Silently fail — badge is supplementary
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [entityType, entityId]);

  if (loading || !activeReq) return null;

  const statusLabel = activeReq.status === 'PENDING' ? 'Pending Review' : activeReq.status === 'APPROVED' ? 'Approved, Awaiting Execution' : activeReq.status;

  return (
    <button
      onClick={() => navigate('/deletion-requests')}
      className="flex items-center gap-2 bg-amber-400/10 border border-amber-400/20 rounded-lg px-3 py-2 hover:bg-amber-400/15 transition-colors"
      data-testid="active-delete-request-badge"
    >
      <Shield className="w-3.5 h-3.5 text-amber-400" />
      <span className="text-xs text-amber-400 font-medium">Deletion Request: {statusLabel}</span>
      <ExternalLink className="w-3 h-3 text-amber-400/60" />
    </button>
  );
}
